import { useState } from "react";
import { SyntaxCode } from "@/office/SyntaxCode";
import { cloudFile } from "@/system/cloud";
import { focusWindow } from "@/system/modules";
import archData from "@/system/arch.json";
import featureMap from "@/system/feature-map.json";

type Step = { text: string; line: number };
type Route = {
  app: string;
  method: string;
  route: string;
  file: string;
  reads: string[];
  writes: string[];
  utils: string[];
  external: string[];
  lines: number;
  steps: Step[];
  upstream: string[];
  downstream: string[];
};
type Arch = {
  apps: { id: string; name: string }[];
  routes: Record<string, Route>;
  tables: Record<string, { readers: string[]; writers: string[]; apps: string[] }>;
};
type MapFeature = { id: string; app: string; page: string; name: string; route: string; method: string };
type MapPage = { id: string; name: string };

const ARCH = archData as unknown as Arch;
const FEATURES = (featureMap as unknown as { features: MapFeature[] }).features;
const MAP_PAGES = (featureMap as unknown as { apps: { id: string; pages: MapPage[] }[] }).apps;

const METHOD_LABEL: Record<string, string> = { GET: "取", POST: "存", PUT: "改", DELETE: "删" };

/** 功能中文名（同一接口可能读写两个接口同名，取先出现的） */
function featureOf(app: string, method: string, route: string): MapFeature | undefined {
  return FEATURES.find((f) => f.app === app && f.method === method && f.route === route);
}

function pageName(app: string, pageId: string) {
  const appInfo = MAP_PAGES.find((a) => a.id === app);
  return appInfo?.pages.find((p) => p.id === pageId)?.name ?? "后台/公共";
}

type CodeState = { path: string; line: number | null; code: string } | null;

export function ArchPage() {
  const [appId, setAppId] = useState(() => localStorage.getItem("jhzz_arch_app") ?? ARCH.apps[0].id);
  const [picked, setPicked] = useState<string>(""); // 选中的接口 key
  const [pickedTable, setPickedTable] = useState<string>("");
  const [code, setCode] = useState<CodeState>(null);
  const [error, setError] = useState("");

  const appRoutes = Object.entries(ARCH.routes).filter(([, r]) => r.app === appId);
  const pages = (MAP_PAGES.find((a) => a.id === appId)?.pages ?? []).map((page) => ({
    ...page,
    keys: appRoutes.filter(([key]) => featureOf(appId, ARCH.routes[key].method, ARCH.routes[key].route)?.page === page.id).map(([key]) => key),
  }));
  const backstage = appRoutes
    .filter(([key]) => !featureOf(appId, ARCH.routes[key].method, ARCH.routes[key].route)?.page)
    .map(([key]) => key);
  const appTables = Object.entries(ARCH.tables).filter(([, t]) => t.apps.includes(appId));

  const pickedRoute = picked ? ARCH.routes[picked] : null;
  const tableInfo = pickedTable ? ARCH.tables[pickedTable] : null;

  const relationOfSelected = new Set<string>(
    pickedRoute ? [...pickedRoute.upstream, ...pickedRoute.downstream, picked] : [],
  );
  const tablesOfSelected = new Set<string>(pickedRoute ? [...pickedRoute.reads, ...pickedRoute.writes] : []);

  function labelOf(key: string) {
    const route = ARCH.routes[key];
    if (!route) return key.replace("table:", "");
    const feature = featureOf(route.app, route.method, route.route);
    return feature?.name ?? `${route.method} ${route.route}`;
  }

  async function openCode(path: string, line: number | null) {
    setError("");
    try {
      const file = await cloudFile(path);
      setCode({ path, line, code: file.code });
    } catch (e) {
      setError(e instanceof Error ? e.message : "读不到文件");
    }
  }

  const COL = { page: 20, route: 300, table: 640 };
  const ROW = 30;
  const width = 900;

  // 接口按页面分组排序：同一页面的接口排在一起，页面节点放到这组的中间，线才不会乱成一团
  const order: { key: string; page: string }[] = [];
  for (const page of pages) for (const key of page.keys) order.push({ key, page: page.id });
  for (const key of backstage) order.push({ key, page: "" });
  const rowOf = (key: string) => 30 + order.findIndex((o) => o.key === key) * ROW + 10;
  const tableRow = (name: string) => 30 + appTables.findIndex(([t]) => t === name) * ROW + 10;
  const height = Math.max(order.length, appTables.length) * ROW + 60;

  const pageNodes = pages
    .map((page) => {
      const idx = order.map((o, i) => (o.page === page.id ? i : -1)).filter((i) => i >= 0);
      const center = idx.length ? (idx[0] + idx[idx.length - 1]) / 2 : 0;
      return { id: page.id, name: page.name, y: 30 + center * ROW, count: page.keys.length };
    })
    .sort((a, b) => a.y - b.y);

  // 页面节点太密就往下推，避免叠字
  for (let i = 1; i < pageNodes.length; i += 1) {
    if (pageNodes[i].y - pageNodes[i - 1].y < 24) pageNodes[i].y = pageNodes[i - 1].y + 24;
  }

  return (
    <div className="flex h-screen flex-col bg-[#0a1017] text-[#cfe3f5]">
      <header className="flex shrink-0 items-center gap-3 border-b border-[#16222e] px-4 py-2.5">
        <span className="text-[13.5px] font-medium">系统架构</span>
        <span className="hidden text-[11.5px] text-[#6e8599] lg:inline">
          从左到右：页面上点一下 → 交给哪个接口 → 动哪张表。点任何一个方块，右边告诉你它由哪些小步骤组成、跟谁联动
        </span>
        <a href="#/map" className="ml-auto rounded border border-[#24384a] px-2.5 py-1 text-[11.5px] text-[#9db3c7] hover:text-[#cfe3f5]">
          功能地图
        </a>
        <a href="#/" className="rounded border border-[#24384a] px-2.5 py-1 text-[11.5px] text-[#9db3c7] hover:text-[#cfe3f5]">
          返回对照
        </a>
      </header>

      <div className="flex shrink-0 items-center gap-2 border-b border-[#16222e] bg-[#0b141c] px-4 py-2">
        {ARCH.apps.map((app) => (
          <button
            key={app.id}
            type="button"
            onClick={() => {
              setAppId(app.id);
              localStorage.setItem("jhzz_arch_app", app.id);
              setPicked("");
              setPickedTable("");
              setCode(null);
            }}
            className={`rounded px-2.5 py-1 text-[12px] ${
              appId === app.id ? "bg-[#17242f] text-[#cfe3f5] ring-1 ring-[#27455f]" : "text-[#69809a] hover:text-[#a9bdcd]"
            }`}
          >
            {app.name}
          </button>
        ))}
        <span className="ml-2 text-[11px] text-[#4e6478]">
          {appRoutes.length} 个接口 · {appTables.length} 张表
        </span>
        <span className="ml-auto text-[11px] text-[#4e6478]">
          整个系统 = 主站（登录/菜单） + 4 个独立子系统，各自一套「页面 → 接口 → 数据库」；外部连着 WPS 云文档（备份）和钉钉（考勤）
        </span>
      </div>

      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1 overflow-auto bg-[#080f16]">
          <svg width={width} height={height} className="block">
            {[
              { x: COL.page, label: "页面（你看到的）" },
              { x: COL.route, label: "后端接口（干活的）" },
              { x: COL.table, label: "数据表（存东西的）" },
            ].map((col) => (
              <text key={col.label} x={col.x} y={16} className="fill-[#4e6478]" fontSize={11}>
                {col.label}
              </text>
            ))}

            {/* 页面 → 接口：只画淡线，选中接口才点亮 */}
            {order.map((item) => {
              const page = pageNodes.find((n) => n.id === item.page);
              if (!page) return null;
              const active = picked === item.key;
              return (
                <path
                  key={`p-${item.key}`}
                  d={`M ${COL.page + 170} ${page.y} C ${COL.page + 240} ${page.y}, ${COL.route - 50} ${rowOf(item.key)}, ${COL.route} ${rowOf(item.key)}`}
                  fill="none"
                  stroke={active ? "#4f8cff" : "#131f29"}
                  strokeWidth={active ? 1.6 : 1}
                />
              );
            })}

            {/* 接口 → 表：只有选中时才画（实线写、虚线读），平时不画，避免糊成一片 */}
            {pickedRoute
              ? [...new Set([...pickedRoute.reads, ...pickedRoute.writes])].map((name) => {
                  if (tableRow(name) <= 0) return null;
                  const write = pickedRoute.writes.includes(name);
                  return (
                    <path
                      key={`t-${name}`}
                      d={`M ${COL.route + 200} ${rowOf(picked)} C ${COL.route + 250} ${rowOf(picked)}, ${COL.table - 40} ${tableRow(name)}, ${COL.table} ${tableRow(name)}`}
                      fill="none"
                      stroke={write ? "#d8a24a" : "#3f9d78"}
                      strokeWidth={1.8}
                      strokeDasharray={write ? undefined : "4 3"}
                    />
                  );
                })
              : null}

            {/* 表被选中时，点亮所有读写它的接口 */}
            {pickedTable
              ? [...(ARCH.tables[pickedTable]?.writers ?? []), ...(ARCH.tables[pickedTable]?.readers ?? [])]
                  .filter((key) => order.some((o) => o.key === key))
                  .map((key) => {
                    const write = (ARCH.tables[pickedTable]?.writers ?? []).includes(key);
                    return (
                      <path
                        key={`sel-${key}`}
                        d={`M ${COL.route + 200} ${rowOf(key)} C ${COL.route + 250} ${rowOf(key)}, ${COL.table - 40} ${tableRow(pickedTable)}, ${COL.table} ${tableRow(pickedTable)}`}
                        fill="none"
                        stroke={write ? "#d8a24a" : "#3f9d78"}
                        strokeWidth={1.4}
                        strokeDasharray={write ? undefined : "4 3"}
                        opacity={0.85}
                      />
                    );
                  })
              : null}

            {/* 页面节点 */}
            {pageNodes.map((page) => (
              <g
                key={page.id}
                onClick={() => {
                  const first = pages.find((p) => p.id === page.id)?.keys[0];
                  if (first) {
                    setPicked(first);
                    setPickedTable("");
                  }
                }}
                style={{ cursor: "pointer" }}
              >
                <rect x={COL.page} y={page.y - 10} width={170} height={20} rx={3} fill="#0e1a24" stroke="#1d2f3f" />
                <text x={COL.page + 8} y={page.y + 4} className="fill-[#9db3c7]" fontSize={11}>
                  {page.name}
                  <tspan className="fill-[#3f5567]" fontSize={9.5}> {page.count}</tspan>
                </text>
              </g>
            ))}

            {/* 接口节点 */}
            {order.map((item, i) => {
              const r = ARCH.routes[item.key];
              const active = picked === item.key;
              const related = relationOfSelected.has(item.key);
              return (
                <g key={item.key} onClick={() => { setPicked(item.key); setPickedTable(""); }} style={{ cursor: "pointer" }}>
                  <rect
                    x={COL.route}
                    y={30 + i * ROW}
                    width={200}
                    height={20}
                    rx={3}
                    fill={active ? "#17242f" : "#0c151d"}
                    stroke={active ? "#4f8cff" : related ? "#2c5c7a" : "#1a2836"}
                  />
                  <text
                    x={COL.route + 6}
                    y={30 + i * ROW + 14}
                    fontSize={10.5}
                    className={active ? "fill-[#e3eefa]" : related ? "fill-[#a9c6dc]" : "#7f97ab"}
                    fill={active ? "#e3eefa" : related ? "#a9c6dc" : "#7f97ab"}
                  >
                    {labelOf(item.key).slice(0, 11)}
                    <tspan fill="#3f5567" fontSize={9.5}> {METHOD_LABEL[r.method]}</tspan>
                  </text>
                </g>
              );
            })}

            {/* 表节点 */}
            {appTables.map(([name, info]) => {
              const y = tableRow(name);
              const active = pickedTable === name;
              const related = tablesOfSelected.has(name);
              return (
                <g key={name} onClick={() => { setPickedTable(name); setPicked(""); }} style={{ cursor: "pointer" }}>
                  <rect
                    x={COL.table}
                    y={y - 10}
                    width={200}
                    height={20}
                    rx={3}
                    fill={active ? "#1d1a10" : "#0c151d"}
                    stroke={active ? "#d8a24a" : related ? "#6b5a2c" : "#1a2836"}
                  />
                  <text
                    x={COL.table + 6}
                    y={y + 4}
                    fontSize={10.5}
                    fill={active ? "#f0cf8a" : related ? "#d3bd8a" : "#8a9fb0"}
                  >
                    {name}
                    <tspan fill="#3f5567" fontSize={9}>
                      {" "}读{info.readers.length} 写{info.writers.length}
                    </tspan>
                  </text>
                </g>
              );
            })}
          </svg>
        </div>

        {/* 右：详情 */}
        <aside className="w-[380px] shrink-0 overflow-auto border-l border-[#16222e] bg-[#0b141c] px-4 py-3">
          {error ? <p className="mb-2 text-[11px] text-[#c0503a]">{error}</p> : null}

          {pickedRoute ? (
            <>
              <h2 className="text-[14px] text-[#e3eefa]">{labelOf(picked)}</h2>
              <p className="mt-0.5 font-mono text-[10.5px] text-[#5f7d94]">
                {pickedRoute.method} {pickedRoute.route} · {pageName(pickedRoute.app, featureOf(pickedRoute.app, pickedRoute.method, pickedRoute.route)?.page ?? "")}
              </p>

              <h3 className="mt-3 text-[11.5px] text-[#69809a]">它按这个顺序干活（共 {pickedRoute.steps.length} 步）</h3>
              <ol className="mt-1 space-y-1">
                {pickedRoute.steps.map((step, i) => (
                  <li key={`${step.line}-${i}`}>
                    <button
                      type="button"
                      onClick={() => openCode(pickedRoute.file, step.line)}
                      className="flex w-full items-start gap-1.5 rounded px-1 py-0.5 text-left hover:bg-[#121c26]"
                    >
                      <span className="mt-[1px] w-4 shrink-0 text-right text-[10px] text-[#3f5567]">{i + 1}</span>
                      <span className="text-[11.5px] leading-[1.6] text-[#a9bdcd]">{step.text}</span>
                      <span className="ml-auto shrink-0 font-mono text-[9.5px] text-[#33505f]">L{step.line}</span>
                    </button>
                  </li>
                ))}
              </ol>

              <h3 className="mt-3 text-[11.5px] text-[#69809a]">谁给它数据（上游）</h3>
              {pickedRoute.upstream.length > 0 ? (
                <div className="mt-1 flex flex-wrap gap-1">
                  {pickedRoute.upstream.map((key) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setPicked(key)}
                      className="rounded border border-[#24384a] px-1.5 py-0.5 text-[10.5px] text-[#9db3c7] hover:border-[#3a5a74] hover:text-[#cfe3f5]"
                    >
                      {labelOf(key)}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="mt-1 text-[11px] text-[#4e6478]">没有别的功能产它的数据（它是源头）</p>
              )}

              <h3 className="mt-3 text-[11.5px] text-[#69809a]">它的结果被谁用（下游）</h3>
              {pickedRoute.downstream.length > 0 ? (
                <div className="mt-1 flex flex-wrap gap-1">
                  {pickedRoute.downstream.map((key) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setPicked(key)}
                      className="rounded border border-[#24384a] px-1.5 py-0.5 text-[10.5px] text-[#9db3c7] hover:border-[#3a5a74] hover:text-[#cfe3f5]"
                    >
                      {labelOf(key)}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="mt-1 text-[11px] text-[#4e6478]">后面的功能不读它的结果（它是终点）</p>
              )}

              {pickedRoute.external.length > 0 ? (
                <p className="mt-3 text-[11px] text-[#d8b96a]">会连外部系统：{pickedRoute.external.join("、")}</p>
              ) : null}

              <button
                type="button"
                onClick={() => openCode(pickedRoute.file, 1)}
                className="mt-3 w-full truncate rounded border border-[#24384a] px-2 py-1 text-left font-mono text-[10.5px] text-[#7fb3ff] hover:border-[#3a5a74]"
              >
                {pickedRoute.file}
              </button>
            </>
          ) : tableInfo ? (
            <>
              <h2 className="text-[14px] text-[#f0cf8a]">{pickedTable}</h2>
              <p className="mt-1 text-[11.5px] leading-[1.7] text-[#9db3c7]">
                这张表有 {tableInfo.readers.length} 个功能在读、{tableInfo.writers.length} 个功能在写。改它的字段，上面这些功能都要一起测。
              </p>
              <h3 className="mt-3 text-[11.5px] text-[#69809a]">谁往里写</h3>
              <div className="mt-1 flex flex-wrap gap-1">
                {tableInfo.writers.map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setPicked(key)}
                    className="rounded border border-[#5a4a24] px-1.5 py-0.5 text-[10.5px] text-[#d3bd8a] hover:border-[#8a6f34]"
                  >
                    {labelOf(key)}
                  </button>
                ))}
              </div>
              <h3 className="mt-3 text-[11.5px] text-[#69809a]">谁从里读</h3>
              <div className="mt-1 flex flex-wrap gap-1">
                {tableInfo.readers.map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setPicked(key)}
                    className="rounded border border-[#24402f] px-1.5 py-0.5 text-[10.5px] text-[#8fc7a8] hover:border-[#3f7a5d]"
                  >
                    {labelOf(key)}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <h2 className="text-[13.5px] text-[#e3eefa]">这张图在说什么</h2>
              <p className="mt-2 text-[11.5px] leading-[1.9] text-[#9db3c7]">
                中间一列是<b className="text-[#a9c6dc]">接口</b>——就是你每点一个按钮，背后真正干活的那段程序。左边是它属于哪个<b>页面</b>，右边是它会动到的<b className="text-[#f0cf8a]">数据表</b>。
              </p>
              <p className="mt-2 text-[11.5px] leading-[1.9] text-[#9db3c7]">
                点一个接口，右边会告诉你：
                <br />1. 它按什么顺序干活（每一步都能点开看代码）
                <br />2. 谁给它数据（上游）
                <br />3. 它的结果被谁用（下游）
              </p>
              <p className="mt-2 text-[11.5px] leading-[1.9] text-[#69809a]">
                图上的线：<span className="text-[#d8a24a]">橙实线＝写这张表</span>，
                <span className="text-[#3f9d78]">绿虚线＝读这张表</span>。平时不画，选中才亮，免得糊成一片。
              </p>
            </>
          )}
        </aside>
      </div>

      {code ? (
        <div className="flex h-[38%] shrink-0 flex-col border-t border-[#16222e] bg-[#080f16]">
          <div className="flex shrink-0 items-center gap-2 border-b border-[#16222e] px-4 py-1.5">
            <span className="min-w-0 truncate font-mono text-[11px] text-[#7fb3ff]">{code.path}</span>
            {code.line ? <span className="shrink-0 text-[10.5px] text-[#4e6478]">第 {code.line} 行附近</span> : null}
            <button
              type="button"
              onClick={() => setCode(null)}
              className="ml-auto rounded border border-[#24384a] px-1.5 py-0.5 text-[10.5px] text-[#9db3c7] hover:text-[#cfe3f5]"
            >
              关闭
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-auto">
            <SyntaxCode
              code={code.line ? focusWindow(code.code, code.line).code : code.code}
              startLine={code.line ? focusWindow(code.code, code.line).start : 1}
              highlightRange={code.line ? { start: Math.max(1, code.line - 1), end: code.line + 1 } : undefined}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
